import { useState, useMemo, useRef, useEffect } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTourTelemetry } from '../Tour.telemetry';

/**
 * Hook for managing tour navigation state and actions
 *
 * Handles:
 * - Current step index state
 * - Finding valid step indices (first, next, previous)
 * - Computing current step name
 * - Computing navigation availability (hasNext, hasPrevious)
 * - Navigation actions (run, stop, next, prev)
 */
export const useTourNavigation = (options: { tourName: string; expectedStepNames: ReadonlyArray<string>; validExpectedSteps: ReadonlySet<string>; onComplete?: () => void }) => {
  const { tourName, expectedStepNames, validExpectedSteps, onComplete } = options;

  const tourTelemetry = useTourTelemetry();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Stable ref for onComplete to avoid dependency issues
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Find the first valid step index in expected order
  const findFirstValidStepIndex = useMemo(() => () => expectedStepNames.findIndex(name => validExpectedSteps.has(name)), [expectedStepNames, validExpectedSteps]);

  // Find the next valid step index from current position
  const findNextValidStepIndex = useMemo(
    () => (fromIndex: number) => expectedStepNames.findIndex((name, i) => i > fromIndex && validExpectedSteps.has(name)),
    [expectedStepNames, validExpectedSteps],
  );

  // Find the previous valid step index from current position
  const findPreviousValidStepIndex = useMemo(
    () => (fromIndex: number) => expectedStepNames.findLastIndex((name, i) => i < fromIndex && validExpectedSteps.has(name)),
    [expectedStepNames, validExpectedSteps],
  );

  // Compute current step name from index and expected order
  const currentStepName = useMemo(() => {
    if (currentStepIndex >= 0 && currentStepIndex < expectedStepNames.length) {
      return expectedStepNames[currentStepIndex];
    }
    return null;
  }, [currentStepIndex, expectedStepNames]);

  // Check if there's a valid next step
  const hasNext = useMemo(() => {
    return findNextValidStepIndex(currentStepIndex) !== -1;
  }, [currentStepIndex, findNextValidStepIndex]);

  // Check if there's a valid previous step
  const hasPrevious = useMemo(() => {
    return findPreviousValidStepIndex(currentStepIndex) !== -1;
  }, [currentStepIndex, findPreviousValidStepIndex]);

  // Start the tour - validate expected steps exist
  const run = useEventCallback(() => {
    const firstValidIndex = findFirstValidStepIndex();
    if (firstValidIndex === -1) {
      console.warn('[Tour] No valid steps found to start tour.');
      return;
    }

    const firstStepName = expectedStepNames[firstValidIndex];
    setCurrentStepIndex(firstValidIndex);
    setIsOpen(true);

    tourTelemetry.tourLaunched.enter(tourName, expectedStepNames, firstValidIndex + 1);
    if (firstStepName) {
      tourTelemetry.tourStepShown.enter(tourName, firstStepName, firstValidIndex + 1);
    }
  });

  // Stop the tour and reset state
  const stop = useEventCallback(() => {
    // Track tour exited before resetting state
    // Compute step name directly from current index to ensure we get the correct value
    const exitedStepName = currentStepIndex >= 0 && currentStepIndex < expectedStepNames.length ? expectedStepNames[currentStepIndex] : null;
    const isLastStep = findNextValidStepIndex(currentStepIndex) === -1;
    tourTelemetry.tourExited.enter(tourName, isLastStep ? 'Completed Tour' : 'Exited Tour', exitedStepName, currentStepIndex + 1);

    setIsOpen(false);
    setCurrentStepIndex(0);
    setIsCollapsed(true);
  });

  // Navigate to next step - skip missing steps
  const next = useEventCallback(() => {
    // Check if there's a next step before updating state
    const nextValidIndex = findNextValidStepIndex(currentStepIndex);
    if (nextValidIndex === -1) {
      // No more valid steps, end tour by calling stop()
      stop();
      onCompleteRef.current?.();
      return;
    }

    // Update to next step
    const nextStepName = expectedStepNames[nextValidIndex];
    setCurrentStepIndex(nextValidIndex);

    if (nextStepName) {
      tourTelemetry.tourStepShown.enter(tourName, nextStepName, nextValidIndex + 1);
    }
  });

  // Navigate to previous step - skip missing steps
  const prev = useEventCallback(() => {
    setCurrentStepIndex(prevIndex => {
      const prevValidIndex = findPreviousValidStepIndex(prevIndex);
      if (prevValidIndex === -1) {
        // No previous valid step - already at first step
        return prevIndex;
      }

      const prevStepName = expectedStepNames[prevValidIndex];
      if (prevStepName) {
        tourTelemetry.tourStepShown.enter(tourName, prevStepName, prevValidIndex + 1);
      }

      return prevValidIndex;
    });
  });

  const handleSetIsCollapsed = useEventCallback((value: boolean) => {
    setIsCollapsed(value);
  });

  return {
    isOpen,
    currentStepIndex,
    currentStepName,
    hasNext,
    hasPrevious,
    isCollapsed,
    run,
    stop,
    next,
    prev,
    setIsCollapsed: handleSetIsCollapsed,
  };
};
